// Authentication
import { BrowserAuthError, BrowserAuthErrorCodes, BrowserCacheLocation, EventMessageUtils, EventType, InteractionRequiredAuthError, InteractionStatus, InteractionType, PublicClientApplication } from "@azure/msal-browser-1p";
import { Logger, PromptValue } from "@azure/msal-browser-uno";
import { broadcastResponseToMainFrame } from "@azure/msal-browser-uno/redirect-bridge";
// Constants
import { Environment, TenantRegion } from "@ms/uno-constants/lib/local/AppConstants";
import { AuthTokenClaims, GuestUserAcctClaimValue, TenantRegionScope, TenantRegionSubScope } from "@ms/uno-constants/lib/local/AuthConstants";
// Errors
import { NoAccountAuthError } from "./NoAccountAuthError";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
// Utilities
import { ErrorUtilities } from "@ms/uno-errors/lib/local/utilities/ErrorUtilities";
import { MarkerTag } from "@ms/uno-telemetry/lib/local/performance/constants/PerformanceMarkerConstants";
import { retry } from "../../RetryUtilities";
import { getClaimsParameter } from "../AuthUtilities";
import { BaseMsalHelper } from "./BaseMsalHelper";
import { MsalCacheLocation } from "./IMsalConfiguration";
import { PromptType } from "./MsalEnums";
import { isLoginRequiredAuthError } from "./MsalUtilities";
/**
 * Helper for authentication using msal.js
 */ export class MsalHelper extends BaseMsalHelper {
    /**
     * Initialize Msal Helper
     * @param msalConfigSettings The partial configuration settings to construct the MSAL object
     * @param msalHelperOptions Options for the MsalHelper
     * @param loggers Logger to send logs
     */ static async initialize(msalConfigSettings, msalHelperOptions, loggers) {
        loggers.traceLogger.logTrace(0x1e04c7c2 /* tag_4bm5c */ , TraceLevel.Info, `MsalHelper initializing`);
        const msalHelper = new MsalHelper(msalConfigSettings, msalHelperOptions, loggers);
        await msalHelper.initializeMsal(msalConfigSettings);
        loggers.performanceMarker.mark(MarkerTag.MSALHelperInitialized, {
            CorrelationId: msalConfigSettings.correlationId
        });
        // Handle redirect promise during app initialization
        await msalHelper.handleAuthRedirect();
        loggers.performanceMarker.mark(MarkerTag.MSALAuthRedirectComplete, {
            CorrelationId: msalConfigSettings.correlationId
        });
        loggers.traceLogger.logTrace(0x1e04c7c1 /* tag_4bm5b */ , TraceLevel.Info, `MsalHelper initialized`);
        return msalHelper;
    }
    /**
     * Initializes the MSAL redirect bridge on the blank redirect page.
     * When the silent auth flow redirects to the blank redirect page (inside a hidden iframe),
     * this method relays the auth response to the main frame via the BroadcastChannel API.
     * Must be called on the blank redirect page before returning — do not render the app.
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/login-user.md
     * @param loggers Logger context
     */ static async initializeRedirectBridge(loggers) {
        loggers.traceLogger.logTrace(0x1d8db38c /* tag_391om */ , TraceLevel.Info, "initializeRedirectBridge: Initializing redirect bridge for blank redirect page.");
        try {
            await broadcastResponseToMainFrame();
            loggers.traceLogger.logTrace(0x1d8db38b /* tag_391ol */ , TraceLevel.Info, "initializeRedirectBridge: Redirect bridge payload sent successfully.");
        } catch (error) {
            loggers.traceLogger.logTrace(0x1d8db38a /* tag_391ok */ , TraceLevel.Warning, MsalHelper.buildErrorMessage({
                error,
                hasClaims: false
            }));
        }
    }
    async createMsalClient(authConfig) {
        try {
            const msalConfig = this.createMsalConfig(authConfig);
            const matsConfiguration = this.createMatsConfiguration(authConfig);
            return await PublicClientApplication.createPublicClientApplication(msalConfig, matsConfiguration, authConfig.correlationId);
        } catch (error) {
            this.loggers.traceLogger.logTrace(0x1e28f21b /* tag_4kpi1 */ , TraceLevel.Error, this.constructErrorMessage({
                error,
                hasClaims: false
            }));
            throw error;
        }
    }
    /**
     * Create the MATs configuration so we log to MATs telemetry
     * @param msalConfigSettings The partial configuration settings to construct the MSAL object
     */ createMatsConfiguration(msalConfigSettings) {
        const { clientName, clientVersion } = msalConfigSettings;
        if (clientName === "" || clientVersion === "") {
            const errorMessage = "MsalHelper: clientName and clientVersion must be provided in msalConfigSettings";
            this.loggers.traceLogger.logTrace(0x1e2df707 /* tag_4l52h */ , TraceLevel.Error, errorMessage);
            throw new Error(errorMessage);
        }
        return {
            appName: clientName,
            appVersion: clientVersion,
            disabled: false
        };
    }
    /**
     * Creates the Msal configuration object
     * @param msalConfigSettings The partial configuration settings to construct the MSAL object
     * @returns Msal Configuration object for the app
     */ createMsalConfig(msalConfigSettings) {
        const { clientId, msalCacheLocation, postLogoutRedirectUrl, correlationId } = msalConfigSettings;
        const postLogoutRedirectUri = postLogoutRedirectUrl.toString();
        const cacheLocation = this.getCacheLocationName(msalCacheLocation);
        // cp1 is the capability for CAE
        const clientCapabilities = this.options.cp1ClientCapability ? [
            "cp1"
        ] : undefined;
        return {
            auth: {
                clientId,
                postLogoutRedirectUri,
                clientCapabilities
            },
            cache: {
                cacheLocation
            },
            system: {
                loggerOptions: this.createLoggerOptions(correlationId)
            }
        };
    }
    /**
     * Returns the cacheLocation name for the MsalCacheLocation
     * @param cacheLocation The MsalCacheLocation
     */ getCacheLocationName(cacheLocation) {
        switch(cacheLocation){
            case MsalCacheLocation.LocalStorage:
                return BrowserCacheLocation.LocalStorage;
            case MsalCacheLocation.SessionStorage:
                return BrowserCacheLocation.SessionStorage;
            case MsalCacheLocation.MemoryStorage:
                return BrowserCacheLocation.MemoryStorage;
            default:
                ErrorUtilities.unreachableCase(cacheLocation, (log)=>{
                    this.loggers.traceLogger.logTrace(0x1e311613 /* tag_4mryt */ , TraceLevel.Warning, log);
                });
                return BrowserCacheLocation.LocalStorage;
        }
    }
    /**
     * Handle redirect response and set the active account.
     */ async handleAuthRedirect() {
        try {
            const response = await this.msalObject.handleRedirectPromise();
            if (response) {
                this.loggers.traceLogger.logTrace(0x1e28774d /* tag_4kh3n */ , TraceLevel.Info, "Received response from handleRedirectPromise.");
                const responseAccount = response.account;
                if (responseAccount) {
                    this.loggers.traceLogger.logTrace(0x1e287723 /* tag_4kh29 */ , TraceLevel.Info, "Received account from handleRedirectPromise.");
                    this.msalObject.setActiveAccount(responseAccount);
                } else {
                    this.loggers.traceLogger.logTrace(0x1e28805e /* tag_4kib4 */ , TraceLevel.Warning, "Received null account for handleRedirectPromise.");
                    throw new NoAccountAuthError(response.requestId);
                }
            }
        } catch (error) {
            this.loggers.traceLogger.logTrace(0x1e28805d /* tag_4kib3 */ , TraceLevel.Warning, this.constructErrorMessage({
                error,
                hasClaims: false
            }));
            throw error;
        }
    }
    /**
     * Checks if the logged in account matches the expected account and logs in if not
     * @param params Parameters to login the user
     */ async checkActiveAccountAndLoginIfRequired(params) {
        const { forceLogin, userId, tenantId, ensureActiveAccount, claimsChallenge } = params || {};
        const hasActiveAccount = this.hasActiveAccount();
        this.loggers.traceLogger.logTrace(0x1e04c7c0 /* tag_4bm5a */ , TraceLevel.Info, `checkActiveAccountAndLoginIfRequired [ForceLogin=${!!forceLogin}][HasUserId=${!!userId}][HasTenantId=${!!tenantId}][EnsureActiveAccount=${!!ensureActiveAccount}][HasClaimsChallenge=${!!claimsChallenge}][HasActiveAccount=${hasActiveAccount}]`);
        const forceLoginOrClaimsChallenge = forceLogin || claimsChallenge;
        // When forceLogin is true or there is a claims challenge, we skip using current cached user and force the user to login
        if (!forceLoginOrClaimsChallenge) {
            if (!ensureActiveAccount && hasActiveAccount) {
                this.loggers.traceLogger.logTrace(0x1e04c7a3 /* tag_4bm49 */ , TraceLevel.Info, "checkActiveAccountAndLoginIfRequired: Skipping login, active account already present.");
                return;
            }
            if (ensureActiveAccount && userId && tenantId && this.validateActiveAccount(userId, tenantId)) {
                // If there is an cached active account then we know the user is already logged-in and no further action is needed.
                this.loggers.traceLogger.logTrace(0x1e04c7a2 /* tag_4bm48 */ , TraceLevel.Info, "checkActiveAccountAndLoginIfRequired: Skipping login, validated active account matches expected user.");
                return;
            }
        }
        this.loggers.traceLogger.logTrace(0x1e04c7a1 /* tag_4bm47 */ , TraceLevel.Info, `checkActiveAccountAndLoginIfRequired: Proceeding to attemptLogin [ForceLoginOrClaimsChallenge=${!!forceLoginOrClaimsChallenge}]`);
        await this.attemptLogin(params);
    }
    /**
     * Evaluates if there is an active account present
     * @returns True if there is an active account
     */ hasActiveAccount() {
        const account = this.msalObject.getActiveAccount();
        return account !== null;
    }
    /**
     * Validate that an active account exists and/or the specified userId/tenantId
     * @param userId Expected userId
     * @param tenantId Expected tenantId
     * @returns True if specified user is logged in
     */ validateActiveAccount(userId, tenantId) {
        const account = this.msalObject.getActiveAccount();
        if (!account) {
            this.loggers.traceLogger.logTrace(0x1e04c7a0 /* tag_4bm46 */ , TraceLevel.Info, `validateActiveAccount: No active account found`);
            return false;
        }
        if (userId && tenantId && userId === account.localAccountId && tenantId === account.tenantId) {
            return true;
        }
        this.loggers.traceLogger.logTrace(0x1e04c79f /* tag_4bm45 */ , TraceLevel.Info, `validateActiveAccount: Active account does not match expected user`);
        return false;
    }
    // #region ensure logged in utilities
    /**
     * Ensure user is logged with with extended properties.
     * 1. We check if we are able to log the user in using loginHint if it is specified.
     * 2. If the user has never logged into uno web, we try sso.
     * 3. If userId/tenantId is specified we filter using them. If an account exists, use it to login.
     * 4. If there is more than 1 account show the account selector, otherwise show the login username page.
     * @params params The parameters to login the user
     */ async attemptLogin(params) {
        const { forceLogin, userId, tenantId, loginHint, claimsChallenge, isNoPromptCheck } = params;
        if (forceLogin || claimsChallenge) {
            this.loggers.traceLogger.logTrace(0x1e04c79e /* tag_4bm44 */ , TraceLevel.Info, `attemptLogin: Forcing login [ForceLogin=${!!forceLogin}][HasClaimsChallenge=${!!claimsChallenge}][HasLoginHint=${!!loginHint}][HasTenantId=${!!tenantId}]`);
            await this.login({
                promptType: PromptType.Login,
                tenantId,
                loginHint,
                claimsChallenge
            });
            return;
        }
        const accounts = this.msalObject.getAllAccounts();
        if (loginHint) {
            // Login hint is usually the user's upn. If we have loginHint, we use the loginRedirect
            // with loginHint specified. This ensures login succeeds on Chrome when SSO extension is not enabled.
            this.loggers.traceLogger.logTrace(0x1e04c79d /* tag_4bm43 */ , TraceLevel.Info, `attemptLogin: Using loginHint path [HasForceLogin=${!!forceLogin}][IsNoPromptCheck=${!!isNoPromptCheck}][HasTenantId=${!!tenantId}][HasUserId=${!!userId}]`);
            await this.findExistingAccountAndLogin({
                promptType: isNoPromptCheck ? undefined : PromptType.None,
                userId,
                tenantId,
                loginHint
            });
        } else if (!accounts || accounts.length === 0) {
            // First-ever visit: no cached accounts, try SSO via hidden iframe.
            // ssoSilent relies on 3rd-party cookies which may be blocked (e.g. Chrome without SSO extension).
            // On failure, fall back to loginRedirect with prompt=none to attempt SSO via full redirect.
            const ssoRequest = {
                scopes: this.getDefaultAppScopes(),
                authority: this.getAuthorityUrl(tenantId),
                redirectUri: this.silentRedirectUri
            };
            try {
                this.loggers.traceLogger.logTrace(0x1e0548d8 /* tag_4bu9y */ , TraceLevel.Info, "Attempting ssoSilent login.");
                const ssoResponse = await this.msalObject.ssoSilent(ssoRequest);
                this.loggers.traceLogger.logTrace(0x1e0548d7 /* tag_4bu9x */ , TraceLevel.Info, "ssoSilent login succeeded.");
                return this.handleSilentLoginSuccess(ssoResponse);
            } catch (ssoError) {
                // ssoSilent failed — likely 3rd-party cookies blocked or no existing AAD session.
                // Fall back to loginRedirect with prompt=none which performs a full redirect to AAD,
                // bypassing 3rd-party cookie restrictions. If AAD has no session, it returns
                // interaction_required which the bootstrap catch block handles by showing the landing page.
                const willFallback = this.options.ssoSilentRedirectFallback && !isNoPromptCheck && !isLoginRequiredAuthError(ssoError);
                this.loggers.traceLogger.logTrace(0x1e0548d6 /* tag_4bu9w */ , TraceLevel.Warning, `ssoSilent failed [willFallback=${willFallback}][ssoSilentRedirectFallback=${!!this.options.ssoSilentRedirectFallback}][isNoPromptCheck=${isNoPromptCheck}][isLoginRequiredError=${isLoginRequiredAuthError(ssoError)}]. ${this.constructErrorMessage({
                    error: ssoError,
                    hasClaims: false
                })}`);
                if (!willFallback) {
                    // Shouldn't retry if disabled or if it is already from retry or if we have explicit error asking for login requirement
                    throw ssoError;
                }
                try {
                    // Attempt acquireTokenRedirect as recommended per documentation: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/login-user.md#silent-login-with-ssosilent
                    // We set "prompt=none" to avoid showing UI for user to enter credentials and show the home page instead
                    await this.attemptAcquireTokenRedirectWithoutPrompt(this.getDefaultAppScopes(), tenantId);
                } catch (loginError) {
                    this.loggers.traceLogger.logTrace(0x1e0548d5 /* tag_4bu9v */ , TraceLevel.Warning, `login redirect without interaction failed. ${this.constructErrorMessage({
                        error: loginError,
                        hasClaims: false
                    })}`);
                    // re-throw the error so that the bootstrap catch block can handle showing the landing page
                    throw loginError;
                }
            }
        } else {
            const account = this.msalObject.getActiveAccount();
            this.loggers.traceLogger.logTrace(0x1e0548d4 /* tag_4bu9u */ , TraceLevel.Info, `Login attempt [HasActiveAccount=${!!account}][UserId=${userId}][TenantId=${tenantId}][AccountsCount=${accounts?.length}][ActiveAccountTenantId=${account?.tenantId}][ActiveAccountUserId=${account?.localAccountId}]`);
            // If we are signed in to a different tenant, sign out and sign in to the correct tenant
            if (account && tenantId && account.tenantId !== tenantId) {
                this.loggers.traceLogger.logTrace(0x1e0548d3 /* tag_4bu9t */ , TraceLevel.Info, "Logout due to tenant mismatch");
                await this.logout();
                return;
            }
            await this.findExistingAccountAndLogin({
                promptType: accounts.length > 1 ? PromptType.SelectAccount : isNoPromptCheck ? undefined : PromptType.None,
                userId,
                tenantId,
                loginHint
            });
        }
    }
    /**
     * Finds an existing account based on the provided parameters and attempts to login.
     * @param params The parameters to use for finding an existing account and logging in
     */ async findExistingAccountAndLogin(params) {
        const { promptType, userId, tenantId, loginHint } = params;
        // If loginHint is provided, we should not use the active account since it could be different
        // from the account associated with the loginHint
        let account = loginHint ? null : this.msalObject.getActiveAccount();
        if (userId || tenantId || loginHint) {
            // Check if an account already exists for the tenantId or userId if it is provided
            account = this.msalObject.getAccount({
                tenantId: tenantId,
                localAccountId: userId,
                loginHint
            });
            this.loggers.traceLogger.logTrace(0x1e04c79c /* tag_4bm42 */ , TraceLevel.Info, `findExistingAccountAndLogin: Account lookup result [FoundAccount=${!!account}]`);
        }
        if (account) {
            await this.attemptSilentLogin(this.getDefaultAppScopes(), account);
        } else {
            await this.login({
                promptType,
                tenantId,
                loginHint
            });
        }
    }
    /**
     * Set the active account after a successful silent login
     * @param response The response from a silent login
     */ handleSilentLoginSuccess(response) {
        if (response) {
            this.loggers.traceLogger.logTrace(0x1e04c79b /* tag_4bm41 */ , TraceLevel.Info, `handleSilentLoginSuccess: Setting active account [HasAccount=${!!response.account}]`);
            this.msalObject.setActiveAccount(response.account);
        } else {
            this.loggers.traceLogger.logTrace(0x1e31761b /* tag_4mxy1 */ , TraceLevel.Warning, "Received null response for silent login.");
            const error = new InteractionRequiredAuthError("NoActiveAccountAfterSilentLogin", "No active account found after silent login.");
            throw error;
        }
    }
    /**
     * Function which ensures the user is either logged in or initiates the
     * login flow in other cases.
     * @param params ILoginParameters object
     */ async ensureLoggedIn(params) {
        // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/errors.md#interaction_in_progress
        // Note: If you are calling loginRedirect or acquireTokenRedirect from a page that is not your redirectUri you will need to ensure
        // handleRedirectPromise is called and awaited on both the redirectUri page as well as the page that you initiated the redirect from.
        // This is because the redirectUri page will initiate a redirect back to the page that originally invoked loginRedirect and that page
        // will process the token response.
        try {
            await this.checkActiveAccountAndLoginIfRequired(params);
            if (!this.hasActiveAccount()) {
                throw new InteractionRequiredAuthError("NoActiveAccountAfterLogin", "No active account found after login.");
            }
            const msalActiveAccount = this.msalObject.getActiveAccount();
            const activeAccount = this.getActiveAccount();
            const { tenant_region_scope, tenant_region_sub_scope, xms_tdbr } = msalActiveAccount?.idTokenClaims ?? {};
            this.loggers.traceLogger.logTrace(0x1e2e204d /* tag_4l8bn */ , TraceLevel.Info, `ensureLoggedIn done [isGuest=${activeAccount?.isGuest}][telemetryRegion=${activeAccount?.telemetryRegion}][accountEnvironment=${activeAccount?.accountEnvironment}][tenantRegion=${tenant_region_scope}][tenantRegionSubScope=${tenant_region_sub_scope}][telemetryDataBoundary=${xms_tdbr}]`);
        } catch (error) {
            const hasClaims = params.claimsChallenge ? true : false;
            this.loggers.traceLogger.logTrace(0x1e2e004f /* tag_4l6bp */ , TraceLevel.Warning, this.constructErrorMessage({
                error,
                hasClaims
            }));
            throw error;
        }
    }
    /**
     * Gets the active account from MSAL Cache
     * @returns { IUserAccount | null } IUserAccount if there is an active account, null otherwise
     */ getActiveAccount() {
        const account = this.msalObject.getActiveAccount();
        if (!account) {
            this.loggers.traceLogger.logTrace(0x1e04c79a /* tag_4bm40 */ , TraceLevel.Info, "getActiveAccount: No active account in MSAL cache.");
            return null;
        }
        let isGuest = false;
        let telemetryRegion = TenantRegion.Row;
        let accountEnvironment = Environment.Prod;
        const { idTokenClaims } = account;
        if (idTokenClaims) {
            if (idTokenClaims[AuthTokenClaims.TelemetryDataBoundary] === TenantRegion.Eu) {
                telemetryRegion = TenantRegion.Eu;
            }
            // User is a guest if the homeAccountId does not contain the tenantId
            // and the idp claim is present in idTokenClaims
            // or claim 'acct' is set to 1.
            if (idTokenClaims.idp) {
                isGuest = !account.homeAccountId.includes(account.tenantId);
            }
            if (idTokenClaims[AuthTokenClaims.Acct] === GuestUserAcctClaimValue) {
                isGuest = true;
            }
            const tenant_region_sub_scope = idTokenClaims.tenant_region_sub_scope;
            if (tenant_region_sub_scope === TenantRegionSubScope.GCC) {
                accountEnvironment = Environment.Gcc;
            } else if (tenant_region_sub_scope === TenantRegionSubScope.DODCON) {
                accountEnvironment = Environment.Gcch;
            } else if (tenant_region_sub_scope === TenantRegionSubScope.DOD) {
                accountEnvironment = Environment.Dod;
            }
            const tenant_region_scope = idTokenClaims.tenant_region_scope;
            if (tenant_region_scope === TenantRegionScope.Bleu) {
                telemetryRegion = TenantRegion.Eu;
                accountEnvironment = Environment.Bleu;
            }
        }
        this.loggers.traceLogger.logTrace(0x1e04c799 /* tag_4bm4z */ , TraceLevel.Info, `getActiveAccount: Returning account`);
        return {
            userId: account.localAccountId,
            tenantId: account.tenantId,
            loginHint: account.username,
            displayName: account.name,
            isGuest,
            telemetryRegion,
            accountEnvironment
        };
    }
    /**
     * Reset the logger after the user has been logged in
     * @param loggers The logger
     * @param appName App name
     * @param clientVersion client version
     * @param correlationId correlationId for MATS
     */ setLogger(loggers, appName, clientVersion, correlationId) {
        this.loggers = loggers;
        const logger = new Logger(this.createLoggerOptions(correlationId), appName, clientVersion);
        this.msalObject.setLogger(logger);
    }
    /**
     * Stop the Msal logger
     */ stopLogger() {
        // Set an empty logger which will not log anything
        const emptyLogger = new Logger({});
        this.msalObject.setLogger(emptyLogger);
    }
    // #region Token Related Function
    /**
     * Try to login in silently. This function will throw an exception on failure.
     * @param scopes Scopes for the endpoint
     * @param [account] Account info object
     * @param [claimsChallenge] Claims for getting the token
     */ async attemptSilentLogin(scopes, account, claimsChallenge) {
        const claims = getClaimsParameter(claimsChallenge, this.loggers.traceLogger);
        const authority = this.getAuthorityUrl(account?.tenantId);
        const request = {
            account,
            scopes,
            claims,
            authority,
            redirectUri: this.silentRedirectUri
        };
        try {
            this.loggers.traceLogger.logTrace(0x1e054797 /* tag_4bu4x */ , TraceLevel.Info, `Login via acquireTokenSilent attempt [HasActiveAccount=${!!account}][AccountTenantId=${account?.tenantId}][AccountUserId=${account?.localAccountId}]`);
            const response = await this.msalObject.acquireTokenSilent(request);
            this.loggers.traceLogger.logTrace(0x1e054796 /* tag_4bu4w */ , TraceLevel.Info, `Login via acquireTokenSilent succeeded [HasResponse=${!!response}]`);
            return this.handleSilentLoginSuccess(response);
        } catch (acquireTokenSilentError) {
            const hasClaims = claims ? true : false;
            this.loggers.traceLogger.logTrace(0x1e29a410 /* tag_4k0qq */ , TraceLevel.Warning, this.constructErrorMessage({
                error: acquireTokenSilentError,
                hasClaims,
                scopes
            }));
            if (acquireTokenSilentError instanceof InteractionRequiredAuthError && this.options.handleInteractionRequiredOnSilentLogin) {
                // During bootstrap user login we retry with redirect as it is more reliable than popup
                this.loggers.traceLogger.logTrace(0x1e04c798 /* tag_4bm4y */ , TraceLevel.Info, `attemptSilentLogin: InteractionRequired, falling back to login redirect [Scopes=${scopes.join(",")}][HasClaims=${!!claims}][ErrorCode=${acquireTokenSilentError.errorCode}][SubError=${acquireTokenSilentError.subError}]`);
                await this.login({
                    promptType: PromptType.Login,
                    account,
                    scopes,
                    claimsChallenge
                });
            } else {
                throw acquireTokenSilentError;
            }
        }
    }
    // #endregion
    /**
     * Login redirect to the app
     * @param params Parameters to login
     */ async login(params) {
        const { promptType, tenantId, loginHint, claimsChallenge, scopes = this.getDefaultAppScopes(), account } = params;
        const prompt = promptType != null ? this.getPromptNameFromType(promptType) : undefined;
        const claims = getClaimsParameter(claimsChallenge, this.loggers.traceLogger);
        const loginRequest = {
            scopes,
            prompt,
            loginHint,
            authority: this.getAuthorityUrl(tenantId),
            claims,
            account,
            redirectUri: this.redirectUri
        };
        this.loggers.traceLogger.logTrace(0x1e11b8dc /* tag_4e192 */ , TraceLevel.Info, `Msal login [Scopes=${scopes?.join(",") ?? ""}][Prompt=${prompt}][HasLoginHint=${!!loginHint}][HasClaims=${!!claims}][UserId=${account?.localAccountId}][UserTenantId=${account?.tenantId}][Authority=${loginRequest.authority}][IsInIframe=${this.isInIframe}]`);
        try {
            // For non-iframe scenarios redirect the user for login
            if (!this.isInIframe) {
                return await this.msalObject.loginRedirect(loginRequest);
            }
            // Otherwise, use a popup based login
            const popupResponse = await this.msalObject.loginPopup(loginRequest);
            const responseAccount = popupResponse?.account;
            if (responseAccount) {
                // Set the account as active
                this.loggers.traceLogger.logTrace(0x1e04c797 /* tag_4bm4x */ , TraceLevel.Info, `login: Popup login succeeded, setting active account`);
                this.msalObject.setActiveAccount(responseAccount);
            } else {
                this.loggers.traceLogger.logTrace(0x1e04c796 /* tag_4bm4w */ , TraceLevel.Warning, `login: Popup login returned no account`);
                throw new NoAccountAuthError(popupResponse.requestId);
            }
        } catch (error) {
            const hasClaims = claims ? true : false;
            this.loggers.traceLogger.logTrace(0x1e2e004c /* tag_4l6bm */ , TraceLevel.Warning, this.constructErrorMessage({
                error,
                hasClaims,
                scopes
            }));
            throw error;
        }
    }
    /**
     * Returns the prompt string value from a PromptType enum
     * @param prompt The PromptType enum value
     * @returns Prompt string for MSAL request
     */ getPromptNameFromType(prompt) {
        switch(prompt){
            case PromptType.Login:
                return PromptValue.LOGIN;
            case PromptType.SelectAccount:
                return PromptValue.SELECT_ACCOUNT;
            case PromptType.None:
                return PromptValue.NONE;
            default:
                ErrorUtilities.unreachableCase(prompt, (log)=>{
                    this.loggers.traceLogger.logTrace(0x1e311612 /* tag_4mrys */ , TraceLevel.Warning, log);
                });
                return PromptValue.NONE;
        }
    }
    /**
     * Helper function to logout the active account
     * @param postLogoutRedirectUrl Redirect URL after logout
     */ async logout(postLogoutRedirectUrl) {
        const account = this.msalObject.getActiveAccount();
        if (!account) {
            this.loggers.traceLogger.logTrace(0x1e11b8db /* tag_4e191 */ , TraceLevel.Warning, "No active account found during logout.");
            return;
        }
        const logoutRequest = {
            account,
            postLogoutRedirectUri: postLogoutRedirectUrl?.toString() ?? undefined,
            authority: this.getAuthorityUrl(account.tenantId)
        };
        try {
            this.loggers.traceLogger.logTrace(0x1e11b8da /* tag_4e190 */ , TraceLevel.Info, `Logout for account [UserId=${account.localAccountId}][TenantId=${account.tenantId}][IsInIframe=${this.isInIframe}]`);
            await this.msalObject.setActiveAccount(null);
            if (this.isInIframe) {
                this.loggers.traceLogger.logTrace(0x1e04c795 /* tag_4bm4v */ , TraceLevel.Info, "logout: Using popup logout for iframe context");
                await this.msalObject.logoutPopup(logoutRequest);
            } else {
                this.loggers.traceLogger.logTrace(0x1e04c78e /* tag_4bm4o */ , TraceLevel.Info, "logout: Using redirect logout.");
                await this.msalObject.logoutRedirect(logoutRequest);
            }
            this.loggers.traceLogger.logTrace(0x1e04c794 /* tag_4bm4u */ , TraceLevel.Info, `logout: Logout completed successfully`);
        } catch (error) {
            this.loggers.traceLogger.logTrace(0x1e2e004b /* tag_4l6bl */ , TraceLevel.Warning, this.constructErrorMessage({
                error,
                hasClaims: false
            }));
            throw error;
        }
    }
    /**
     * Register a function to be called when an interaction required error occurs
     * @param callback The function to be called when an interaction required error occurs
     */ registerInteractionRequiredErrorHandler(callback) {
        this.msalObject.addEventCallback((message)=>{
            const { eventType, error, interactionType } = message;
            // In MSAL 5, LOGIN_FAILURE and SSO_SILENT_FAILURE were consolidated into ACQUIRE_TOKEN_FAILURE
            const isInteractionRequiredTypeEvent = eventType === EventType.ACQUIRE_TOKEN_FAILURE;
            if (!isInteractionRequiredTypeEvent) {
                return;
            }
            const isGetAccessTokenSilentFailure = eventType === EventType.ACQUIRE_TOKEN_FAILURE && interactionType === InteractionType.Silent;
            if (this.options.handleInteractionRequiredOnSilentLogin && isGetAccessTokenSilentFailure) {
                // if enableSilentSignInPopup is set then the acquire token failure is handled during getAccessToken
                return;
            }
            if (this.options.handleInteractionRequiredOnSilentLogin && eventType === EventType.ACQUIRE_TOKEN_FAILURE && interactionType === InteractionType.Popup && error instanceof BrowserAuthError && error.errorCode === BrowserAuthErrorCodes.popupWindowError) {
                const status = EventMessageUtils.getInteractionStatusFromEvent(message);
                if (status === null || status === InteractionStatus.None) {
                    this.loggers.traceLogger.logTrace(0x1e29a387 /* tag_4k0oh */ , TraceLevel.Warning, `[Msal] popup blocked. ${this.constructErrorMessage({
                        error,
                        hasClaims: false
                    })}`);
                    return;
                }
            }
            if (error instanceof InteractionRequiredAuthError) {
                const status = EventMessageUtils.getInteractionStatusFromEvent(message);
                if (status === null || status === InteractionStatus.None) {
                    const claimsChallenge = error?.claims;
                    this.loggers.traceLogger.logTrace(0x1e307203 /* tag_4mhid */ , TraceLevel.Info, `[Msal] User interaction required to login. ${this.constructErrorMessage({
                        error,
                        hasClaims: !!claimsChallenge
                    })}`);
                    callback(/* claims */ claimsChallenge, /* isPopupBlockedError*/ false);
                }
            }
        });
    }
    /**
     * Register a function to be called when browser popup blocked error occurs during acquireTokenPopup
     * @param callback The function to be called when browser popup blocked error occurs
     */ registerPopupBlockedErrorHandler(callback) {
        if (callback === null || callback === undefined) {
            return;
        }
        this.loggers.traceLogger.logTrace(0x1e28b607 /* tag_4klyh */ , TraceLevel.Info, `[Msal] Registering popup blocked error handler.`);
        this.handlePopupBlockedErrorCallback = callback;
    }
    /**
     * We will fetch main scopes and evaluate conditional access policies with login redirect
     * We can avoid acquire token with popup and use redirect since we are loading the app and popup might be blocked by browsers by default
     */ async prefetchAndEvaluateConditionalAccess(prefetchScopes) {
        for (const scope of prefetchScopes){
            try {
                await this.attemptSilentTokenFetchWithRetry(scope);
            } catch (error) {
                // This is likely a NOOP since if actually fails it will attempt to login with redirect navigation
                this.loggers.traceLogger.logTrace(0x1e29820e /* tag_4kyio */ , TraceLevel.Warning, `Prefetch failure: ${this.constructErrorMessage({
                    error,
                    hasClaims: !!error.claims,
                    scopes: [
                        scope
                    ]
                })}`);
            }
        }
    }
    /**
     * Attempts to silently fetch token for given scope and login with redirect.
     * This method retries on retriable msal errors
     * @param scope the scope to attempt login
     */ async attemptSilentTokenFetchWithRetry(scope) {
        this.loggers.traceLogger.logTrace(0x1e04c793 /* tag_4bm4t */ , TraceLevel.Info, `attemptSilentTokenFetchWithRetry: Starting retry loop [Scope=${scope}]`);
        const scopes = [
            scope
        ];
        const maxTokenFetchRetryCount = 3;
        await retry(async ()=>await this.attemptSilentLogin(scopes, this.msalObject.getActiveAccount() ?? undefined), maxTokenFetchRetryCount - 1, /* interval */ 0, this.canRetry);
    }
    /**
     * Checks if can retry given msal auth error
     * @param error the error to retry
     * @returns Returns TRUE if can retry
     */ canRetry(error) {
        const errorCode = error?.errorCode;
        return errorCode === BrowserAuthErrorCodes.timedOut || errorCode === "temporarily_unavailable" || errorCode === "endpoints_resolution_error";
    }
    /**
     * Login scopes for app
     * @returns Default app scopes for login
     */ getDefaultAppScopes() {
        return [
            "openid",
            "profile",
            `${this.clientId}/.default`
        ];
    }
    /**
     * Handle the popup blocked error
     * @param scopes Scope for the resource
     * @param claimsChallenge claims challenge parameter
     */ async handlePopupBlockedError(scopes, claimsChallenge) {
        if (!this.handlePopupBlockedErrorCallback) {
            this.loggers.traceLogger.logTrace(0x1e04c792 /* tag_4bm4s */ , TraceLevel.Warning, `handlePopupBlockedError: No popup blocked callback registered, cannot recover [Scopes=${scopes.join(",")}][HasClaims=${!!claimsChallenge}]`);
            return;
        }
        this.loggers.traceLogger.logTrace(0x1e04c791 /* tag_4bm4r */ , TraceLevel.Info, `handlePopupBlockedError: Invoking popup blocked callback [Scopes=${scopes.join(",")}][HasClaims=${!!claimsChallenge}]`);
        this.handlePopupBlockedErrorCallback(scopes, async ()=>await this.acquireTokenPopupHelper(scopes, this.userInitiatedAcquireTokenPopup.bind(this), claimsChallenge));
    }
    /**
     * User initiated acquire the token for the set of specified scopes
     * @param scopes Scopes for the resource
     * @param [claimsChallenge] Claims for getting the token
     */ async userInitiatedAcquireTokenPopup(scopes, claimsChallenge) {
        this.loggers.traceLogger.logTrace(0x1e04c790 /* tag_4bm4q */ , TraceLevel.Info, `userInitiatedAcquireTokenPopup: Starting [Scopes=${scopes.join(",")}][HasClaims=${!!claimsChallenge}]`);
        const prompt = this.getPromptNameFromType(PromptType.Login);
        const claims = getClaimsParameter(claimsChallenge, this.loggers.traceLogger);
        const account = this.msalObject.getActiveAccount();
        if (!account) {
            this.loggers.traceLogger.logTrace(0x1e04c78f /* tag_4bm4p */ , TraceLevel.Warning, "userInitiatedAcquireTokenPopup: No active account found, throwing NoAccountAuthError.");
            throw new NoAccountAuthError();
        }
        const tenantId = account.tenantId;
        const acquireTokenRequest = {
            scopes,
            prompt,
            authority: this.getAuthorityUrl(tenantId),
            claims,
            account,
            redirectUri: this.redirectUri
        };
        try {
            await this.msalObject.acquireTokenRedirect(acquireTokenRequest);
        } catch (error) {
            const hasClaims = claims ? true : false;
            this.loggers.traceLogger.logTrace(0x1e28b419 /* tag_4klqz */ , TraceLevel.Warning, this.constructErrorMessage({
                error,
                hasClaims,
                scopes
            }));
            throw error;
        }
    }
    /**
     * Attempts to acquire a token via redirect with prompt=none (no user interaction).
     * Used as a fallback when ssoSilent fails due to 3rd-party cookie restrictions.
     * @param scopes Scopes for the endpoint
     * @param tenantId tenantId for authority url
     */ async attemptAcquireTokenRedirectWithoutPrompt(scopes, tenantId) {
        this.loggers.traceLogger.logTrace(0x1e018761 /* tag_4ay37 */ , TraceLevel.Info, `attemptAcquireTokenRedirectWithoutPrompt: Starting [Scopes=${scopes.join(",")}]`);
        const prompt = this.getPromptNameFromType(PromptType.None);
        const acquireTokenRequest = {
            scopes,
            prompt,
            authority: this.getAuthorityUrl(tenantId),
            redirectUri: this.redirectUri
        };
        try {
            await this.msalObject.acquireTokenRedirect(acquireTokenRequest);
        } catch (error) {
            this.loggers.traceLogger.logTrace(0x1e018760 /* tag_4ay36 */ , TraceLevel.Warning, this.constructErrorMessage({
                error,
                hasClaims: false,
                scopes
            }));
            throw error;
        }
    }
    constructor(authConfig, options, loggers){
        super(authConfig, loggers, options), this.handlePopupBlockedErrorCallback = null;
        this.clientId = authConfig.clientId;
        this.isInIframe = self !== top;
    }
}
